// src/components/HeatingList.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchFolderManifest } from '../data/excelFileManifest';
import ExcelReader from '../services/ExcelReader';

const HeatingList = () => {
  const { t, language } = useLanguage();
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    system: 'all',
    status: 'all',
    startDate: '',
    endDate: ''
  });
  const [pendingFilters, setPendingFilters] = useState({
    system: 'all',
    status: 'all',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    let isMounted = true;

    const loadFiles = async () => {
      const manifestFiles = await fetchFolderManifest('Isitma');
      if (isMounted) {
        setFiles(manifestFiles);
      }
    };

    loadFiles();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadRows = async () => {
      setIsLoading(true);

      if (files.length === 0) {
        if (isMounted) {
          setRows([]);
          setIsLoading(false);
        }
        return;
      }

      const results = await Promise.all(
        files.map(async (filePath) => {
          const parts = filePath.split('/');
          const folder = parts.length > 1 ? parts[0] : '';
          const fileName = parts.length > 1 ? parts.slice(1).join('/') : filePath;
          const result = await ExcelReader.readHeatingFile(fileName, folder);
          return {
            filePath,
            result
          };
        })
      );

      if (!isMounted) {
        return;
      }

      const mappedRows = results.flatMap(({ filePath, result }) =>
        result.success
          ? result.data.map((row, index) => ({
              ...row,
              id: row.id || `${filePath}-${index + 1}`,
              index: index + 1
            }))
          : []
      );

      setRows(mappedRows);
      setIsLoading(false);
    };

    loadRows();

    return () => {
      isMounted = false;
    };
  }, [files]);

  const availableSystems = useMemo(() => {
    const systems = new Set(rows.map((row) => row.system).filter(Boolean));
    return Array.from(systems).sort((a, b) => a.localeCompare(b, locale));
  }, [rows, locale]);

  const availableStatuses = useMemo(() => {
    const statuses = new Set(rows.map((row) => row.status).filter(Boolean));
    return Array.from(statuses).sort((a, b) => a.localeCompare(b, locale));
  }, [rows, locale]);

  const toUtcDate = (value) => (value ? new Date(`${value}T00:00:00Z`) : null);

  const filteredRows = useMemo(() => {
    const startDate = toUtcDate(filters.startDate);
    const endDate = toUtcDate(filters.endDate);

    return rows.filter((row) => {
      if (filters.system !== 'all' && row.system !== filters.system) {
        return false;
      }
      if (filters.status !== 'all' && row.status !== filters.status) {
        return false;
      }
      if (startDate || endDate) {
        if (!row.timestamp) {
          return false;
        }
        if (startDate && row.timestamp < startDate) {
          return false;
        }
        if (endDate && row.timestamp > endDate) {
          return false;
        }
      }
      return true;
    });
  }, [filters, rows]);

  const formatDateTime = (date) => {
    if (!date) {
      return '-';
    }
    return date.toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC'
    });
  };

  const statusLabel = (status) => {
    if (status === 'attention') {
      return 'Uyarı';
    }
    if (status === 'normal') {
      return 'Normal';
    }
    return status || '-';
  };

  return (
    <div className="space-y-6">
      <header className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{t('lists.heating.title')}</h2>
            <p className="text-slate-700">{t('lists.heating.subtitle')}</p>
          </div>
          <div className="text-sm text-slate-500">
            {t('lists.totalFiles', { count: files.length.toLocaleString(locale) })}
          </div>
        </div>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 text-sm font-semibold text-slate-700 sm:flex-row sm:items-center sm:justify-between">
          <span>{t('lists.heating.filters.title')}</span>
          <span className="text-xs font-medium text-slate-500">
            {t('lists.heating.filters.subtitle')}
          </span>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-[1.1fr_1.6fr_1fr_auto] xl:items-end">
          <label className="flex flex-col gap-2 text-sm text-slate-600">
            <span className="font-medium text-slate-700">{t('lists.heating.filters.system')}</span>
            <select
              value={pendingFilters.system}
              onChange={(event) =>
                setPendingFilters((prev) => ({ ...prev, system: event.target.value }))
              }
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="all">{t('lists.heating.filters.all')}</option>
              {availableSystems.map((system) => (
                <option key={system} value={system}>
                  {system}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-col gap-2 text-sm text-slate-600">
            <span className="font-medium text-slate-700">{t('lists.heating.filters.date')}</span>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-2 text-xs text-slate-500">
                <span className="font-medium text-slate-600">
                  {t('lists.heating.filters.startDate')}
                </span>
                <input
                  type="date"
                  value={pendingFilters.startDate}
                  onChange={(event) =>
                    setPendingFilters((prev) => ({ ...prev, startDate: event.target.value }))
                  }
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </label>
              <label className="flex flex-col gap-2 text-xs text-slate-500">
                <span className="font-medium text-slate-600">
                  {t('lists.heating.filters.endDate')}
                </span>
                <input
                  type="date"
                  value={pendingFilters.endDate}
                  onChange={(event) =>
                    setPendingFilters((prev) => ({ ...prev, endDate: event.target.value }))
                  }
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </label>
            </div>
          </div>
          <label className="flex flex-col gap-2 text-sm text-slate-600">
            <span className="font-medium text-slate-700">{t('lists.heating.filters.status')}</span>
            <select
              value={pendingFilters.status}
              onChange={(event) =>
                setPendingFilters((prev) => ({ ...prev, status: event.target.value }))
              }
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="all">{t('lists.heating.filters.all')}</option>
              {availableStatuses.map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-end md:col-span-2 xl:col-span-1">
            <button
              type="button"
              onClick={() => setFilters(pendingFilters)}
              className="w-full rounded-xl bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 xl:w-auto"
            >
              {t('lists.heating.filters.apply')}
            </button>
          </div>
        </div>
      </section>

      {files.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-slate-900">{t('lists.empty.title')}</p>
          <p className="mt-2 text-sm text-slate-600">{t('lists.empty.description')}</p>
        </div>
      ) : isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <p className="text-sm font-medium text-slate-700">Isıtma verileri yükleniyor...</p>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-semibold text-slate-900">{t('lists.empty.title')}</p>
          <p className="mt-2 text-sm text-slate-600">{t('lists.empty.description')}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">
                  {t('lists.heating.table.timestamp')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">
                  {t('lists.heating.table.system')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">
                  {t('lists.heating.table.inletTemp')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">
                  {t('lists.heating.table.outletTemp')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">
                  {t('lists.heating.table.deltaT')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">
                  {t('lists.heating.table.pressure')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">
                  {t('lists.heating.table.status')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-600">{formatDateTime(row.timestamp)}</td>
                  <td className="px-4 py-3 text-slate-900 font-medium">{row.system || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{row.inletTemp}</td>
                  <td className="px-4 py-3 text-slate-600">{row.outletTemp}</td>
                  <td className="px-4 py-3 text-slate-600">{row.deltaT}</td>
                  <td className="px-4 py-3 text-slate-600">{row.pressure}</td>
                  <td className="px-4 py-3 text-slate-600">{statusLabel(row.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HeatingList;
